import Immutable from 'immutable';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { trackAnalyticsEvent } from 'app/actions/AnalyticsGa4Actions/actions';
import {
  eventNames,
  METADATA_CATEGORY_TYPE,
  UI_COMPONENT_TYPE,
  UI_CONTAINER_TYPE
} from 'app/constants/AnalyticsGa4Constants';
import { BROWSE_CATEGORY_PAGE_FACET_FIELD_IDS, FILTERABLE_DISPLAY_BIB_GRID_LIMIT } from 'app/constants/BrowseConstants';
import { deserializeQuerystring, serializeQuerystring } from 'app/helpers/browse/browseQueryHelpers';
import { expandCategoryPath } from 'app/helpers/browse/categories';
import { mapDisplayBibToAnalyticsBibEntity } from 'app/helpers/displayBib';
import useBrowseFacets from 'app/hooks/useBrowseFacets';
import { uiCategoryConfigMapShape } from 'app/shapes/browse';
import FilterableDisplayBibGrid from '../shared/FilterableDisplayBibGrid';

const BrowseCategoryLeafPageContainer = ({ browseCategoryConfig }) => {
  const dispatch = useDispatch();

  const searchQueryParams = browseCategoryConfig?.get('searchQueryParams', Immutable.Map()).toJS() ?? {};
  const baseApiParams = {
    ...searchQueryParams,
    limit: FILTERABLE_DISPLAY_BIB_GRID_LIMIT,
    view: 'grouped'
  };

  const {
    displayAvailabilityByMetadataId,
    displayBibs,
    facetFields,
    handleFilterChange,
    isFetching,
    pagination,
    setShowMoreFieldId,
    unapplyAllFilters
  } = useBrowseFacets({
    baseApiParams,
    baseQuerystring: '',
    deserializeQuerystring,
    includedFieldIds: BROWSE_CATEGORY_PAGE_FACET_FIELD_IDS,
    serializeQuerystring
  });

  const intl = useIntl();

  const appliedFiltersAccessibilityHeading = intl.formatMessage({ id: 'browse_active_filters' });
  const facetsHeading = intl.formatMessage({
    defaultMessage: 'Browse Filters',
    id: 'browse_facets'
  });

  if (!facetFields) {
    return null;
  }

  const categoryPaths = expandCategoryPath(browseCategoryConfig.get('categoryPath'));
  const browseEntity = {
    browse_category: browseCategoryConfig.getIn(['categoryByPath', categoryPaths[0], 'title']),
    browse_category_1: browseCategoryConfig.getIn(['categoryByPath', categoryPaths[1], 'title']),
    browse_category_2: browseCategoryConfig.getIn(['categoryByPath', categoryPaths[2], 'title'])
  };

  const trackAuthorClick = ({ author, displayBib, itemPosition }) => {
    const { count: numTotalItems, page } = pagination?.toJS() ?? {};

    const { metadataId } = displayBib;
    const displayAvailability = displayAvailabilityByMetadataId[metadataId];

    dispatch(
      trackAnalyticsEvent({
        entities: {
          bib: mapDisplayBibToAnalyticsBibEntity(displayBib, displayAvailability),
          browse: browseEntity,
          metadata: {
            metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_CREATORS,
            metadata_value: author
          },
          ui: {
            ui_component_label: author,
            ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
            ui_container_type: UI_CONTAINER_TYPE.ITEM,
            ui_content_page: page,
            ui_content_position: itemPosition,
            ui_content_total_count: numTotalItems
          }
        },
        eventName: eventNames.CATALOG_EXPLORE
      })
    );
  };

  const trackBibLinkClick = (displayBib, title, isImage, itemPosition) => {
    const { count: numTotalItems, page } = pagination?.toJS() ?? {};

    const { metadataId } = displayBib;
    const displayAvailability = displayAvailabilityByMetadataId[metadataId];

    dispatch(
      trackAnalyticsEvent({
        entities: {
          bib: mapDisplayBibToAnalyticsBibEntity(displayBib, displayAvailability),
          browse: browseEntity,
          metadata: {
            metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
            metadata_value: title
          },
          ui: {
            ...(isImage ? {} : { ui_component_label: title }),
            ui_component_type: isImage ? UI_COMPONENT_TYPE.LINK_IMAGE : UI_COMPONENT_TYPE.LINK_TEXT,
            ui_container_type: UI_CONTAINER_TYPE.ITEM,
            ui_content_page: page,
            ui_content_position: itemPosition,
            ui_content_total_count: numTotalItems
          }
        },
        eventName: eventNames.CONTENT_CLICK
      })
    );
  };

  return (
    <div className="container">
      <FilterableDisplayBibGrid
        appliedFiltersAccessibilityHeading={appliedFiltersAccessibilityHeading}
        displayBibs={displayBibs}
        facetFields={facetFields}
        facetsHeading={facetsHeading}
        handleFilterChange={handleFilterChange}
        hasBibRatings
        isFetching={isFetching}
        pagination={pagination}
        setShowMoreFieldId={setShowMoreFieldId}
        trackAuthorClick={trackAuthorClick}
        trackBibLinkClick={trackBibLinkClick}
        unapplyAllFilters={unapplyAllFilters}
      />
    </div>
  );
};

BrowseCategoryLeafPageContainer.propTypes = {
  browseCategoryConfig: uiCategoryConfigMapShape.isRequired
};

export default BrowseCategoryLeafPageContainer;
